import { useState } from 'react'
import { Alert, AppLayout, Button, Card, RequireSession } from 'vcode-commons-ui'
import BankIcon from '../components/BankIcon'
import { useApproveBank, useBanks, usePendingBanks, useReplaceBank, type Bank, type PendingBank } from '../lib/banking'

type Notice = { tone: 'success' | 'error'; text: string }

export default function AdminBanksPage() {
  return (
    <RequireSession adminOnly>
      {(user) => (
        <AppLayout user={user}>
          <h1 className="text-2xl font-semibold tracking-tight text-neutral-900 dark:text-neutral-50">
            Banques a valider
          </h1>
          <p className="mt-2 text-sm text-neutral-600 dark:text-neutral-400">
            Banques ajoutees par les utilisateurs. Validez-les (en corrigeant le nom au besoin) pour les proposer a
            tous, ou remplacez-les par une banque existante en cas de doublon : les comptes suivent.
          </p>
          <PendingList />
        </AppLayout>
      )}
    </RequireSession>
  )
}

function PendingList() {
  const pending = usePendingBanks()
  const banks = useBanks()
  const [notice, setNotice] = useState<Notice | null>(null)

  if (pending.isPending || banks.isPending) {
    return <p className="mt-6 text-sm text-neutral-500">Chargement...</p>
  }

  if (pending.isError || banks.isError) {
    return (
      <div className="mt-6">
        <Alert>{(pending.error ?? banks.error)?.message}</Alert>
      </div>
    )
  }

  const approved = banks.data.filter((bank) => bank.status === 'approved')

  return (
    <div className="mt-6 space-y-4">
      {notice !== null && <Alert tone={notice.tone}>{notice.text}</Alert>}

      {pending.data.length === 0 ? (
        <Card>
          <p className="text-sm text-neutral-600 dark:text-neutral-400">Aucune banque en attente.</p>
        </Card>
      ) : (
        pending.data.map((bank) => (
          <PendingRow key={bank.bankId} bank={bank} approved={approved} onNotice={setNotice} />
        ))
      )}
    </div>
  )
}

function PendingRow({
  bank,
  approved,
  onNotice,
}: {
  bank: PendingBank
  approved: Bank[]
  onNotice: (notice: Notice | null) => void
}) {
  const approve = useApproveBank()
  const replace = useReplaceBank()
  const [name, setName] = useState(bank.name)
  const [replacementId, setReplacementId] = useState('')
  const busy = approve.isPending || replace.isPending

  const onApprove = () => {
    onNotice(null)
    approve.mutate(
      { bankId: bank.bankId, name: name.trim() },
      {
        onSuccess: () => onNotice({ tone: 'success', text: `« ${name.trim()} » validee : elle est proposee a tous.` }),
        onError: (error) => onNotice({ tone: 'error', text: error.message }),
      },
    )
  }

  const onReplace = () => {
    const target = approved.find((candidate) => candidate.bankId === replacementId)
    if (target === undefined) {
      return
    }

    onNotice(null)
    replace.mutate(
      { bankId: bank.bankId, replacementBankId: target.bankId },
      {
        onSuccess: (result) =>
          onNotice({
            tone: 'success',
            text: `« ${bank.name} » remplacee par « ${target.name} » (${result.movedAccounts} compte(s) rattache(s)).`,
          }),
        onError: (error) => onNotice({ tone: 'error', text: error.message }),
      },
    )
  }

  const input =
    'w-full rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm text-neutral-900 outline-none transition focus:border-neutral-900 focus:ring-2 focus:ring-neutral-900/10 dark:border-neutral-700 dark:bg-neutral-950 dark:text-neutral-100 dark:focus:border-neutral-100'

  return (
    <Card>
      <div className="flex items-start gap-3">
        <BankIcon bank={{ name: bank.name, code: null }} />
        <div className="min-w-0">
          <div className="font-medium text-neutral-900 dark:text-neutral-100">{bank.name}</div>
          <div className="text-sm text-neutral-600 dark:text-neutral-400">
            Ajoutee par {bank.proposedBy ?? 'un utilisateur supprime'} le{' '}
            {new Intl.DateTimeFormat('fr-FR', { dateStyle: 'short' }).format(new Date(bank.createdAt))} ·{' '}
            {bank.accountCount} compte(s)
          </div>
        </div>
      </div>

      <div className="mt-4 grid gap-4 md:grid-cols-2">
        <div className="space-y-2">
          <label className="block text-sm font-medium text-neutral-800 dark:text-neutral-200">
            Nom valide
            <input
              value={name}
              maxLength={80}
              onChange={(event) => setName(event.target.value)}
              className={`mt-1.5 ${input}`}
            />
          </label>
          <Button type="button" size="sm" onClick={onApprove} disabled={busy || name.trim().length < 2}>
            Valider
          </Button>
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-neutral-800 dark:text-neutral-200">
            Remplacer par
            <select
              value={replacementId}
              onChange={(event) => setReplacementId(event.target.value)}
              className={`mt-1.5 ${input}`}
            >
              <option value="">Choisir une banque existante...</option>
              {approved.map((candidate) => (
                <option key={candidate.bankId} value={candidate.bankId}>
                  {candidate.name}
                </option>
              ))}
            </select>
          </label>
          <Button type="button" size="sm" variant="outline" onClick={onReplace} disabled={busy || replacementId === ''}>
            Remplacer
          </Button>
        </div>
      </div>
    </Card>
  )
}
